import { Flex, Grid, GridItem } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikSelect } from 'components/Formik';
import Title from 'components/Title';
import { useFetchReportsByWeekTotal } from 'hooks/reports/useFetchReportsByWeeklyTotal';
import { useFetchWeekIntervals } from 'hooks/reports/useFetchWeekIntervals';

import ReportsByWeekTotalTable from './ReportsByWeekTotalTable';
import { initialReportsByWeekTotalFormData, reportsByWeekTotalValidationSchema } from './schema';

const ReportsByWeekTotal: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);

  const formikRef = useRef<any>();

  const [enableFetchWeekInterval, setEnableFetchWeekInterval] = useState<boolean>(false);

  const { data: weeklyData, isInitialLoading } = useFetchWeekIntervals({
    enabled: enableFetchWeekInterval,
  });
  const { data, status, mutateAsync } = useFetchReportsByWeekTotal();

  const handleFormSubmit = (values: typeof initialReportsByWeekTotalFormData) => {
    const postData = {
      start_date: values.date_range !== null ? format(new Date(values.date_range?.first), 'MM-dd-yyyy') : '',
      end_date: values.date_range !== null ? format(new Date(values.date_range?.last), 'MM-dd-yyyy') : '',
    };

    mutateAsync(postData);
  };

  return (
    <div>
      <Title>{t('reports:weekTotals')}</Title>
      <Flex mt="5" justifyContent="center">
        <Formik
          initialValues={initialReportsByWeekTotalFormData}
          validationSchema={reportsByWeekTotalValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {formikBag => {
            formikRef.current = formikBag;
            return (
              <Form autoComplete="off">
                <Grid display="inline-grid" templateColumns={{ base: '1fr', sm: '280px 1fr' }} gap={{ base: 0, sm: 4 }}>
                  <GridItem>
                    <FormikSelect
                      options={weeklyData?.payload || []}
                      label={t('reports:payPeriod')}
                      name="date_range"
                      selectProps={{
                        isLoading: isInitialLoading,
                        onMenuOpen: () => setEnableFetchWeekInterval(true),
                        getOptionLabel: (option: any) => option?.toShow,
                        getOptionValue: (option: any) => option?.toShow,
                        placeholder: t('reports:selectPayPeriod'),
                        isClearable: false,
                      }}
                    />
                  </GridItem>
                  <GridItem mt={{ base: 0, sm: '6' }}>
                    <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                      {t('reports:generateReport')}
                    </Button>
                  </GridItem>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Flex>
      {data ? (
        <ReportsByWeekTotalTable
          data={data?.payload}
          dateRange={[
            new Date(formikRef?.current?.values?.date_range.first),
            new Date(formikRef?.current?.values?.date_range.last),
          ]}
        />
      ) : null}
    </div>
  );
};

export default ReportsByWeekTotal;
